import { FieldbookMap } from "@/components/common/FieldbookMap"
import { useTrackDelete } from "@/hooks/useTrackDelete"
import { useTrackDisplayPrefs } from "@/hooks/useTrackDisplayPrefs"
import { useTrackRecorder } from "@/hooks/useTrackRecorder"
import { Badge } from "@fieldbook/ui/badge"
import { Button } from "@fieldbook/ui/button"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Trash } from "lucide-react"
import { Switch } from "@fieldbook/ui/switch"
import { Layer, Source } from "react-map-gl/mapbox"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
} from "@fieldbook/ui/alert-dialog"
import { Spinner } from "@fieldbook/ui/spinner"
import { useOpenClose } from "@fieldbook/ui/hooks"
import { Label } from "@fieldbook/ui/label"
import { queryClient } from "@/lib/queryClient"
import { trackDetailQueryOptions } from "@/lib/tracks/trackDetail"
import { useSuspenseQuery } from "@tanstack/react-query"

export const Route = createFileRoute(
  "/_private/projects/$id/data/tracks/$trackId",
)({
  component: RouteComponent,
  loader: ({ params: { trackId } }) =>
    queryClient.ensureQueryData(trackDetailQueryOptions(trackId)),
})

function RouteComponent() {
  return (
    <div>
      <div className="flex flex-col">
        <button
          onClick={() => history.back()}
          className="text-muted-foreground mb-4 mt-2 flex items-center gap-1 text-sm focus-visible:outline-0"
        >
          <ArrowLeft size={12} />
          <span>Back</span>
        </button>

        <TrackDetail />
      </div>
    </div>
  )
}

const DeleteTrackModal = ({
  trackId,
  projectId,
  isOpen,
  onClose,
}: {
  trackId: string
  projectId: string
  isOpen: boolean
  onClose: () => void
}) => {
  const { mutateAsync: deleteTrackMutation, isPending: isDeleting } =
    useTrackDelete()

  const handleDelete = async () => {
    await deleteTrackMutation({ id: trackId, projectId })
    onClose()
  }

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogHeader>Delete Track</AlertDialogHeader>
      <AlertDialogContent>
        <p>
          Are you sure you want to delete this track? This action cannot be
          undone.
        </p>
        <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={handleDelete}
          disabled={isDeleting}
          className="bg-red-600 hover:bg-red-50"
        >
          {isDeleting && <Spinner />}
          {!isDeleting && "Delete"}
        </AlertDialogAction>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function TrackDetail() {
  const { id: projectId, trackId } = Route.useParams()
  const { data: track } = useSuspenseQuery(trackDetailQueryOptions(trackId))
  const isActive = Boolean(track) && track?.properties.status !== "completed"

  const { isPending: isDeleting } = useTrackDelete()
  const { open, isOpen, close } = useOpenClose()
  const navigate = useNavigate()
  const { isShown, setShown } = useTrackDisplayPrefs()
  const recorder = useTrackRecorder(track?.properties.createdBy)

  const onDelete = () => {
    close()
    navigate({ to: "/projects/$id/data/tracks", params: { id: projectId } })
  }

  if (!track) {
    return <div className="p-4">Track not found</div>
  }

  // Only the recorder can delete a track (the server refuses anyone else), so
  // a teammate's track — or one naming no recorder — is view-only here.
  const canDelete = Boolean(recorder?.isMe)
  return (
    <div>
      <div className="mb-2 flex flex-col gap-2">
        <div className="flex w-full justify-between">
          <span className="text-xl">
            Track {new Date(track.properties.startTime).toLocaleString()}
          </span>
          {isActive && <Badge>Active</Badge>}
        </div>
        <div className="flex w-full items-center justify-between">
          <div className="text-muted-foreground">
            {track.properties.lengthM.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}{" "}
            m
          </div>
          <div className="flex items-center gap-1">
            <Label>
              Display on Map
              <Switch
                checked={isShown(trackId)}
                onCheckedChange={(checked) => setShown(trackId, checked)}
              />
            </Label>
            {canDelete && (
              <Button
                onClick={() => open()}
                disabled={isDeleting}
                variant="outline"
              >
                <Trash size={16} />
              </Button>
            )}
          </div>
        </div>
        {recorder && !recorder.isMe && (
          <span className="text-muted-foreground flex items-center gap-1 text-sm">
            <span
              aria-hidden
              className="size-3 rounded-full"
              style={{ backgroundColor: recorder.color }}
            />
            Recorded by {recorder.name ?? "a teammate"}
          </span>
        )}
      </div>

      <FieldbookMap
        geoJSON={track}
        withLocation={false}
        fallbackZoom={17}
        fitPadding={20}
      >
        <Source id="active-track" type="geojson" data={track}>
          <Layer
            id="track-line"
            type="line"
            paint={{
              "line-color": recorder?.color ?? "#13ebc3",
              "line-width": 3,
            }}
          />
        </Source>
      </FieldbookMap>
      {isOpen && (
        <DeleteTrackModal
          trackId={trackId}
          projectId={projectId}
          isOpen={isOpen}
          onClose={onDelete}
        />
      )}
    </div>
  )
}
